import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../primitives/card";
import { Button } from "../primitives/button";
import { Input } from "../primitives/input";
import { Label } from "../primitives/label";
import { useSupplierAuth } from "./SupplierAuthProvider";
import {
  buildManualSupplierInfo,
  isReservedSupplierId,
  normalizeMobilePhone,
} from "./test-profiles";
import { toast } from "sonner";

interface SupplierLoginPageProps {
  onSuccess?: () => void;
}

/**
 * 공급사 직접 입력 로그인.
 *
 * - 같은 공급사 ID 로 다시 로그인해도 회사 정보가 바뀌지 않는다(`buildManualSupplierInfo` 참고).
 * - 실제 업체 카드 목록은 두지 않는다 — 시험원에게 실존 업체 정보가 보이고, 그 ID 로 로그인하면
 *   실제 업체 정보가 바뀔 수 있었다(V-02). 실제 업체 ID 는 직접 입력으로도 막는다.
 */
export const SupplierLoginPage: React.FC<SupplierLoginPageProps> = ({
  onSuccess,
}) => {
  const { login, isLoading } = useSupplierAuth();
  const [manualId, setManualId] = useState("");
  const [manualCompanyName, setManualCompanyName] = useState("");
  const [manualPhoneNumber, setManualPhoneNumber] = useState("");

  const handleManualLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const supplierId = manualId.trim();
    const companyName = manualCompanyName.trim();

    if (!supplierId || !companyName) {
      toast.error("공급사 ID와 회사명을 입력해주세요.");
      return;
    }

    if (isReservedSupplierId(supplierId)) {
      toast.error(
        "이 공급사 ID는 실제 업체 계정이라 여기서 로그인할 수 없습니다. 다른 ID를 입력해주세요.",
      );
      return;
    }

    let managerPhone: string | null = null;
    if (manualPhoneNumber.trim()) {
      managerPhone = normalizeMobilePhone(manualPhoneNumber);
      if (!managerPhone) {
        toast.error("휴대폰 번호를 010-1234-5678 형식으로 입력해주세요.");
        return;
      }
    }

    const supplierInfo = buildManualSupplierInfo({
      supplierId,
      companyName,
      managerPhone,
    });
    const success = await login(supplierInfo);

    if (!success) {
      toast.error("로그인하지 못했습니다. 잠시 후 다시 시도해주세요.");
    } else {
      toast.success("로그인되었습니다.");
      onSuccess?.();
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-semibold tracking-tight text-primary">
            세모 판매자 센터
          </h1>
          <p className="text-muted-foreground">
            공급사 ID와 회사명을 입력해 로그인하세요.
          </p>
        </div>

        <Card className="border-primary/20 shadow-md">
          <CardHeader>
            <CardTitle>공급사 로그인</CardTitle>
            <CardDescription>
              같은 공급사 ID로 다시 로그인하면 등록된 회사 정보가 그대로
              유지됩니다.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => void handleManualLogin(e)}
              className="space-y-4"
              noValidate
            >
              <div className="space-y-2">
                <Label htmlFor="supplierId">공급사 ID</Label>
                <Input
                  id="supplierId"
                  placeholder="예: seunghoon"
                  autoComplete="username"
                  maxLength={50}
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyName">회사명</Label>
                <Input
                  id="companyName"
                  placeholder="예: 승훈컴퍼니"
                  autoComplete="organization"
                  maxLength={100}
                  value={manualCompanyName}
                  onChange={(e) => setManualCompanyName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phoneNumber">
                  담당자 휴대폰 번호 (선택 · 알림톡 수신)
                </Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  inputMode="numeric"
                  placeholder="예: 010-1234-5678"
                  maxLength={13}
                  value={manualPhoneNumber}
                  onChange={(e) => setManualPhoneNumber(e.target.value)}
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "처리 중..." : "로그인"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
